import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { ROUTES } from 'routes/routes';
import { Form } from 'antd';
import { OrganizationDetailSection } from './OrganizationDetailSection';
import { AddressBookSection } from './AddressBookSection';
import {
  CreateExternalCompany,
  OrganizationDetailSectionFormData,
} from 'types/organizationTypes';

import styles from '../Organizations.module.css';
import { useUI } from 'contexts/UiContext';
import {
  useCreateExternalOrganizationWithoutKey,
  useGetAddressesForCompany,
  useGetTargetCompanyOrganizationById,
  useUpdateExternalOrganizationWithoutKey,
} from 'api/services/organizationService';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useCreateOrganizationFormContext } from 'contexts/CreateOrganizationFormContext';
import { extractQueryData } from 'utils/services';
import { DarwinSpin } from 'components/darwin-ui';
import { ErrorType } from 'types/genericTypes';
import { EudrRegulationSection } from './EudrRegulationSection';
type CreateOrganizationForm = {
  formAction?: 'create' | 'edit' | 'view';
};

const defaultValuesOnForm = {
  name: '',
  fiscalId: '',
  description: '',
};

const NewOrganization = ({
  formAction = 'create',
}: CreateOrganizationForm): JSX.Element => {
  const isCreateForm = formAction === 'create';

  const isEditForm = formAction === 'edit';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const [organizationKeyDefaultValue, setOrganizationKeyDefaultValue] =
    useState<string>('');
  const { selectedCompany, userPrimaryCompany } = useUI();
  const { setCompanyData } = useCreateOrganizationFormContext();
  const {
    mutateAsync: createExternalOrganizationWithoutKey,
    isPending: isCreatingExternalOrganizationWithoutKeyPending,
  } = useCreateExternalOrganizationWithoutKey();
  const {
    mutateAsync: updateExternalOrganizationWithoutKey,
    isPending: isUpdatingExternalOrganizationWithoutKeyPending,
  } = useUpdateExternalOrganizationWithoutKey();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty, isSubmitting, isValid },
    getValues,
  } = useForm<OrganizationDetailSectionFormData>({
    defaultValues: {
      ...defaultValuesOnForm,
    },
    mode: 'onChange',
  });

  // get initial data when formAction = view | edit
  const { id: targetCompanyId } = useParams();
  const targetCompanyByIdQuery = useGetTargetCompanyOrganizationById(
    selectedCompany || '',
    targetCompanyId || ''
  );

  const targetCompanyByIdData = extractQueryData(
    targetCompanyByIdQuery,
    undefined
  );

  const name = watch('name').toString();
  const fiscalId = watch('fiscalId').toString();

  const addressesForTargetCompanyByIdQuery = useGetAddressesForCompany({
    companyId: selectedCompany || '',
    targetCompanyId: targetCompanyId || '',
    page: 1,
    limit: 100,
  });
  const addressesForTargetCompanyByIdData = extractQueryData(
    addressesForTargetCompanyByIdQuery,
    undefined
  );

  const markCurrentValuesAsClean = (
    values?: OrganizationDetailSectionFormData
  ) => {
    const v = values ?? getValues();
    reset(v, {
      keepDirty: false,
      keepTouched: false,
      keepIsSubmitted: false,
      keepSubmitCount: false,
    });
  };

  useEffect(() => {
    if (!isCreateForm && targetCompanyByIdData) {
      const { name, fiscalId, description } = targetCompanyByIdData;
      const editPayload = { name, fiscalId, description };

      reset(editPayload, {
        keepDirty: false,
        keepTouched: false,
        keepIsSubmitted: false,
        keepSubmitCount: false,
      });

      if (targetCompanyByIdData.isPrimary) {
        setOrganizationKeyDefaultValue(targetCompanyByIdData.organizationKey);
      }
    }
  }, [isCreateForm, reset, targetCompanyByIdData]);

  // submit logic
  const onSubmit = async (
    data: OrganizationDetailSectionFormData,
    saveAndExit?: Boolean,
    onSuccess?: () => void
  ) => {
    const { name, fiscalId, description } = data;
    const payload: CreateExternalCompany = {
      name,
      description,
      fiscalId,
      contractAddress: userPrimaryCompany?.contractAddress || '',
    };

    // create logic
    if (isCreateForm) {
      try {
        const response = await createExternalOrganizationWithoutKey({
          companyData: payload,
          companyId: selectedCompany || '',
        });

        openNotification(
          t('success'),
          t('create-organization-form.success-message'),
          'success'
        );

        setCompanyData(response.data);

        if (onSuccess) onSuccess();
        if (!saveAndExit) {
          navigate(
            generatePath(ROUTES.ORGANIZATIONS.EDIT, {
              id: response.data.id,
            })
          );
        }
        if (saveAndExit) navigate(ROUTES.ORGANIZATIONS.LIST);
      } catch (err) {
        const apiError = err as ErrorType;

        if (apiError.body?.errorCode) {
          openNotification(t('error'), t(apiError.body.errorCode), 'error');
        } else {
          openNotification(
            t('error'),
            t('create-organization-form.error-message'),
            'error'
          );
        }
      }
    }

    // edit logic
    if (isEditForm) {
      if (onSuccess) {
        onSuccess();
        return;
      }

      try {
        await updateExternalOrganizationWithoutKey({
          companyData: payload,
          companyId: selectedCompany || '',
          targetCompanyId: targetCompanyId || '',
        });

        openNotification(
          t('success'),
          t('create-organization-form.edit-success-message'),
          'success'
        );
        if (saveAndExit) navigate(ROUTES.ORGANIZATIONS.LIST);
        if (!saveAndExit) markCurrentValuesAsClean(data);
      } catch {
        openNotification(
          t('error'),
          t('create-organization-form.edit-error-message'),
          'error'
        );
      }
    }
  };

  const handleFormSubmitWithCallback = (callback?: () => void) => {
    return handleSubmit(data => onSubmit(data, false, callback));
  };

  const handleSave = handleSubmit(data => onSubmit(data));
  const handleSaveAndExit = handleSubmit(data => onSubmit(data, true));

  const resetOrganizationDetailFormData = (
    data: OrganizationDetailSectionFormData
  ) => {
    reset(data);
  };

  const disableSaveButtons =
    isCreatingExternalOrganizationWithoutKeyPending ||
    isUpdatingExternalOrganizationWithoutKeyPending ||
    isSubmitting ||
    !isDirty ||
    !isValid;

  return (
    <>
      <DarwinSpin
        spinning={
          targetCompanyByIdQuery.isLoading ||
          addressesForTargetCompanyByIdQuery.isLoading
        }
        fullscreen
      />
      <ContentHeader
        title={isCreateForm ? t('new-organization') : t('edit-organization')}
        onSave={handleSave}
        onSaveTitle={t('organization.create-organization.save-button')}
        onCancel={() => {
          navigate(ROUTES.ORGANIZATIONS.LIST);
        }}
        saveDisabled={disableSaveButtons}
        extraButtonDisabled={disableSaveButtons}
        displayExtraButton={true}
        extraButtonTitle={t('save-exit')}
        onExtraButtonClick={handleSaveAndExit}
      />

      <Form className={styles.organizationFormContainer} layout="vertical">
        <OrganizationDetailSection
          control={control}
          errors={errors}
          resetOrganizationDetailFormData={resetOrganizationDetailFormData}
          organizationKeyDefaultValue={organizationKeyDefaultValue}
        />
      </Form>

      {!isCreateForm && (
        <EudrRegulationSection
          data={targetCompanyByIdData}
          targetCompanyId={targetCompanyId || ''}
          name={name}
          fiscalId={fiscalId}
        />
      )}

      {!isCreateForm && (
        <AddressBookSection
          handleCreateOrganization={callback =>
            handleFormSubmitWithCallback(callback)()
          }
          addresses={addressesForTargetCompanyByIdData}
          isCreateForm={isCreateForm}
          targetCompanyId={targetCompanyId || ''}
        />
      )}
    </>
  );
};

export default NewOrganization;
